/**
 * Admin API Client
 * Handles all admin panel API calls
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

class AdminApiClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Get admin token from localStorage
   */
  getAdminToken() {
    return localStorage.getItem('adminToken');
  }

  /**
   * Set admin token in localStorage
   */
  setAdminToken(token) {
    localStorage.setItem('adminToken', token);
  }

  /**
   * Remove admin token from localStorage
   */
  removeAdminToken() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  }

  /**
   * Get stored admin user
   */
  getAdminUser() {
    const user = localStorage.getItem('adminUser');
    return user ? JSON.parse(user) : null;
  }

  /**
   * Set admin user in localStorage
   */
  setAdminUser(user) {
    localStorage.setItem('adminUser', JSON.stringify(user));
  }

  /**
   * Check if admin is authenticated
   */
  isAuthenticated() {
    return !!this.getAdminToken();
  }

  /**
   * Make authenticated request
   */
  async request(endpoint, options = {}) {
    const token = this.getAdminToken();
    
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    };

    const response = await fetch(`${this.baseUrl}/admin${endpoint}`, config);
    
    // Handle 401/403 - redirect to login
    if (response.status === 401 || response.status === 403) {
      this.removeAdminToken();
      window.location.href = '/admin/login';
      throw new Error('Session expired');
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  }

  // ==================== Auth ====================

  async login(email, password) {
    const response = await fetch(`${this.baseUrl}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    // Response is { token, admin } directly
    this.setAdminToken(data.token);
    this.setAdminUser(data.admin);
    
    return data;
  }

  async logout() {
    this.removeAdminToken();
  }

  async getMe() {
    return this.request('/me');
  }

  // ==================== Settings ====================

  async getSettings(category = null) {
    const query = category ? `?category=${category}` : '';
    return this.request(`/settings${query}`);
  }

  async getSetting(key) {
    return this.request(`/settings/${key}`);
  }

  async createSetting(data) {
    return this.request('/settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSetting(key, data) {
    return this.request(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSetting(key) {
    return this.request(`/settings/${key}`, {
      method: 'DELETE',
    });
  }

  async refreshCache() {
    return this.request('/settings/refresh-cache', {
      method: 'POST',
    });
  }

  // ==================== A/B Tests ====================

  async getABTests() {
    return this.request('/ab-tests');
  }

  async upsertABTest(data) {
    return this.request('/ab-tests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== Users ====================

  async searchUsers(options = {}) {
    const params = new URLSearchParams();
    if (options.query) params.set('query', options.query);
    if (options.page) params.set('page', options.page);
    if (options.limit) params.set('limit', options.limit);
    if (options.sortBy) params.set('sortBy', options.sortBy);
    if (options.sortOrder) params.set('sortOrder', options.sortOrder);
    
    return this.request(`/users?${params.toString()}`);
  }

  async getUserDetails(userId) {
    return this.request(`/users/${userId}`);
  }

  async getUserPreferences(userId) {
    return this.request(`/users/${userId}/preferences`);
  }

  async getUserMealPlans(userId, options = {}) {
    const params = new URLSearchParams();
    if (options.page) params.set('page', options.page);
    if (options.limit) params.set('limit', options.limit);
    
    return this.request(`/users/${userId}/meal-plans?${params.toString()}`);
  }

  async updateUser(userId, data) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ==================== Analytics ====================

  async getDashboardStats() {
    return this.request('/analytics/dashboard');
  }

  async getUserGrowthData(days = 30) {
    return this.request(`/analytics/users/growth?days=${days}`);
  }

  async getMealPlanGrowthData(days = 30) {
    return this.request(`/analytics/meal-plans/growth?days=${days}`);
  }

  // ==================== Admin Management ====================

  async getAllAdmins() {
    return this.request('/admins');
  }

  async createAdmin(data) {
    return this.request('/admins', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== Push Notifications ====================

  async getPushStats() {
    return this.request('/push/stats');
  }

  async sendPushNotification({ title, body, url, target }) {
    return this.request('/push/send', {
      method: 'POST',
      body: JSON.stringify({ title, body, url, target: target || 'all' }),
    });
  }

  // ==================== Promo Meals ====================

  async getPromoMeals() {
    return this.request('/promo');
  }

  async updatePromoMealVisualDescription(promoMealId, visualDescription) {
    return this.request(`/promo/${promoMealId}/visual-description`, {
      method: 'PUT',
      body: JSON.stringify({ visualDescription }),
    });
  }

  async deletePromoMeal(promoMealId) {
    return this.request(`/promo/${promoMealId}`, {
      method: 'DELETE',
    });
  }
}

const adminApi = new AdminApiClient();
export default adminApi;
